import { useController } from '@framework/form/hooks/useController.ts';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useWidgetScopeContext } from '../../../../form/fillable/WidgetScopeContext.tsx';
import { useWidgetEnabled } from '../../../hooks/useWidgetEnabled.ts';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { AdvancedSelectRenderValue } from '../advanced-select-common/AdvancedSelectRenderValue.tsx';
import { AdvancedSelectWidgetComponent } from '../advanced-select-common/AdvancedSelectWidgetComponent.tsx';
import { type IRuntimeWidgetAdvancedOption } from '../advanced-select-common/IAdvancedSelectWidget.ts';
import { getUniqueOptionValue } from '../advanced-select-common/utils/geUniqueOptionValue.ts';
import { getWidgetProps } from '../common/util/getWidgetProps.ts';
import { multiSelectOptionsObjectName } from '../multi-select/getMultiSelectOptionsObjectName.ts';
import { AdvancedMultiSelectWidgetModal } from './AdvancedMultiSelectWidgetModal.tsx';
import { type IAdvancedMultiSelectWidget } from './IAdvancedMultiSelectWidget.ts';

export interface IAdvancedMultiSelectWidgetComponentProps
  extends IWidgetComponentProps<IAdvancedMultiSelectWidget> {}

/**
 * Widget per la selezione multipla avanzata
 */
export function AdvancedMultiSelectWidgetComponent(
  props: IAdvancedMultiSelectWidgetComponentProps,
) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const { prefix } = useWidgetScopeContext();

  const objectFieldName = useMemo(
    () => `${prefix}${multiSelectOptionsObjectName(widget.code)}`,
    [prefix, widget.code],
  );
  // Teniamo `useController` per la registrazione del field nel form context
  useController(objectFieldName);
  useController(options.name);

  const isEnabled = useWidgetEnabled(widget, options);

  const objectFieldValue = useWatch({ name: objectFieldName });

  const value = useMemo(() => {
    const optionsObject = objectFieldValue || {};
    const selectedOptionIds = Object.entries(optionsObject)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);

    // TODO Ottimizzare con una mappa
    return selectedOptionIds
      .map(optionId => {
        const option = widget.options.find(
          o => getUniqueOptionValue(widget, o) === optionId,
        );
        if (!option) return null;

        return {
          ...option,
          id: optionId,
        };
      })
      .filter(item => item !== null) as IRuntimeWidgetAdvancedOption[];
  }, [objectFieldValue, widget]);

  const formItemNames = useMemo(
    () => [objectFieldName, options.name],
    [objectFieldName, options.name],
  );

  const renderComponent = useMemo(() => {
    return (
      <AdvancedSelectRenderValue
        type="multiple"
        valueMultiple={value}
        labelFieldColumns={widget.labelFieldColumns}
        columns={widget.columns}
        placeholder={options.placeholder}
      />
    );
  }, [value, widget.labelFieldColumns, widget.columns, options.placeholder]);

  return (
    <AdvancedSelectWidgetComponent
      {...props}
      formItemName={formItemNames}
      renderComponent={renderComponent}
      renderModal={
        <AdvancedMultiSelectWidgetModal
          {...props}
          currentOption={objectFieldValue}
          readonly={options.readonly || !isEnabled}
        />
      }
    />
  );
}
